import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import api from '../services/api';
import * as shrApi from '../services/shr.api';

const BTN = 'bg-[#039089] hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-xl text-xs disabled:opacity-50';
const BTN2 = 'bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-semibold py-2 px-4 rounded-xl text-xs disabled:opacity-50';
const INP = 'w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-2 text-sm';

function unwrapList(r: any): any[] {
  const d = r?.data ?? r;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.patients)) return d.patients;
  if (Array.isArray(d?.data)) return d.data;
  return [];
}

function downloadJson(filename: string, data: any, mime = 'application/fhir+json') {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: mime });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

function copyText(text: string) {
  return navigator.clipboard.writeText(text);
}

export default function ShrPage() {
  const [dash, setDash] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<any[]>([]);
  const [patient, setPatient] = useState<any>(null);
  const [visits, setVisits] = useState<any[]>([]);
  const [encounterId, setEncounterId] = useState('');
  const [visitType, setVisitType] = useState<'OUTPATIENT' | 'INPATIENT' | 'EMERGENCY'>('OUTPATIENT');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [previewErr, setPreviewErr] = useState('');
  const [selResource, setSelResource] = useState<any>(null);
  const [share, setShare] = useState<any>(null);
  const [copied, setCopied] = useState('');
  const [dhaToken, setDhaToken] = useState<any>(null);

  const loadDash = async () => {
    try { const r = await shrApi.shrDashboard(); setDash(r.data); }
    catch (e: any) { setMsg(e?.response?.data?.message || e.message); }
  };
  useEffect(() => { loadDash(); }, []);

  const search = useCallback(async () => {
    if (q.trim().length < 2) { setHits([]); return; }
    try {
      const r = await api.searchPatients(q.trim());
      setHits(unwrapList(r));
    } catch { setHits([]); }
  }, [q]);
  useEffect(() => { const t = setTimeout(search, 300); return () => clearTimeout(t); }, [search]);

  const pickPatient = async (p: any) => {
    setPatient(p);
    setHits([]);
    setPreview(null);
    setShare(null);
    setSelResource(null);
    setPreviewErr('');
    try {
      const r = await api.getPatientVisits(p.id);
      const list = unwrapList(r);
      setVisits(list);
      const first = list[0];
      setEncounterId(first?.id || first?.reference_id || first?.consultation_id || '');
    } catch {
      setVisits([]);
      setEncounterId('');
    }
  };

  const body = () => ({
    patientId: patient.id,
    encounterId: encounterId || patient.id,
    visitType,
    includeAllPatientData: true,
  });

  const generate = async () => {
    if (!patient?.id) return;
    setBusy(true);
    setPreviewErr('');
    setSelResource(null);
    setShare(null);
    try {
      const r = await shrApi.shrPreviewBundle(body());
      setPreview(r.data);
    } catch (e: any) {
      setPreview(null);
      const d = e?.response?.data;
      setPreviewErr(d?.message || (Array.isArray(d?.errors) ? d.errors.join('; ') : e.message));
      if (d?.mappingIssues || d?.errors) setPreview({ errors: d.errors || [], mappingIssues: d.mappingIssues || [], bundle: null, resources: [] });
    }
    setBusy(false);
  };

  const publishShare = async () => {
    if (!patient?.id) return;
    setBusy(true);
    setPreviewErr('');
    try {
      if (!preview?.bundle) await generate();
      const r = await shrApi.shrPublishShare({ ...body(), ttlHours: 72 });
      setShare(r.data);
      if (!preview?.bundle) {
        const p = await shrApi.shrPreviewBundle(body());
        setPreview(p.data);
      }
    } catch (e: any) {
      setPreviewErr(e?.response?.data?.message || e.message);
    }
    setBusy(false);
  };

  const flash = async (label: string, text: string) => {
    await copyText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const createDhaToken = async () => {
    setBusy(true);
    setPreviewErr('');
    try {
      const r = await shrApi.shrIssueDhaToken({ ttlDays: 7 });
      setDhaToken(r.data);
      await flash('Bearer token', r.data.access_token);
    } catch (e: any) {
      setPreviewErr(e?.response?.data?.issue?.[0]?.diagnostics || e?.response?.data?.message || e.message);
    }
    setBusy(false);
  };

  const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/$/, '');
  const clinicalExample = share?.urls?.clinicalStyle?.find((x: any) => x.type === 'Patient')?.url
    || `${apiBase}/v1/clinical/fhir/Patient/{id}`;
  const curlExample = dhaToken?.access_token
    ? `curl -i -H "Authorization: Bearer ${dhaToken.access_token}" "${clinicalExample}"`
    : `curl -i -H "Authorization: Bearer <paste-token>" "${clinicalExample}"`;

  const errors: string[] = preview?.errors || [];
  const issues: any[] = preview?.mappingIssues || [];
  const omitted: any[] = preview?.omitted || [];
  const entries = preview?.bundle?.entry || [];
  const generated = !!preview?.bundle && entries.length > 0;
  const ok = generated && errors.length === 0 && issues.length === 0;
  const cr = patient?.shaBeneficiaryCrId || patient?.sha_beneficiary_cr_id;

  const resourceCounts = entries.reduce((acc: Record<string, number>, en: any) => {
    const t = (en.resource || en).resourceType;
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  const verifyChecklist = (() => {
    if (!generated) return [];
    const res = entries.map((e: any) => e.resource || e);
    const has = (t: string) => res.some((r: any) => r.resourceType === t);
    const patRes = res.find((r: any) => r.resourceType === 'Patient');
    const org = res.find((r: any) => r.resourceType === 'Organization');
    const conds = res.filter((r: any) => r.resourceType === 'Condition');
    const srs = res.filter((r: any) => r.resourceType === 'ServiceRequest');
    const meds = res.filter((r: any) => r.resourceType === 'MedicationRequest');
    const allergies = res.filter((r: any) => r.resourceType === 'AllergyIntolerance');
    const obs = res.filter((r: any) => r.resourceType === 'Observation');
    const birthOk = !patRes?.birthDate || /^\d{4}-\d{2}-\d{2}$/.test(String(patRes.birthDate));
    const orgOk = org?.name && !/your hospital name|replace with/i.test(String(org.name));
    const icdOk = conds.every((c: any) => (c.code?.coding || []).some((x: any) => String(x.system || '').includes('id.who.int/icd') && x.code));
    const srCoded = srs.filter((s: any) => (s.code?.coding || []).length > 0).length;
    const srTextOnly = srs.length - srCoded;
    const geOk = meds.every((m: any) => (m.medicationCodeableConcept?.coding || []).some((x: any) => String(x.code || '').startsWith('GE')));
    const acOk = allergies.every((a: any) => (a.code?.coding || []).some((x: any) => String(x.code || '').startsWith('AC')));
    const loincObs = obs.filter((o: any) => (o.code?.coding || []).some((x: any) => x.system === 'http://loinc.org')).length;
    return [
      { ok: has('Patient') && birthOk, label: `Patient + birthDate date-only${patRes?.birthDate ? ` (${patRes.birthDate})` : ''}` },
      { ok: !!orgOk, label: `Organization registered name (${org?.name || 'missing'})` },
      { ok: has('Practitioner') && has('EpisodeOfCare') && has('Encounter'), label: 'Practitioner + EpisodeOfCare + Encounter' },
      { ok: conds.length === 0 || icdOk, label: `Condition ICD-11 MMS (${conds.length})` },
      { ok: obs.length === 0 || loincObs === obs.length, label: `Observations LOINC (${loincObs}/${obs.length})` },
      { ok: srs.length === 0 || srTextOnly === 0, label: `ServiceRequest coded (INV/LOINC) — ${srCoded} coded, ${srTextOnly} text-only` },
      { ok: meds.length === 0 || geOk, label: `MedicationRequest GE* (${meds.length})` },
      { ok: allergies.length === 0 || acOk, label: `AllergyIntolerance AC* (${allergies.length})` },
      { ok: errors.length === 0 && issues.length === 0, label: 'No validation / terminology gaps' },
    ];
  })();

  return (
    <Layout title="Kenya SHR">
      <div className="max-w-6xl mx-auto space-y-4">
        {msg && <p className="text-sm text-red-700">{msg}</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ['Enabled', dash?.enabled ? 'Yes' : 'No'],
            ['Facility ID', dash?.facilityId || 'Not set'],
            ['Pending sync', dash?.pending ?? '—'],
            ['Failed', dash?.failed ?? '—'],
          ].map(([k, v]) => (
            <div key={String(k)} className="bg-white border border-slate-200 rounded-2xl p-4">
              <p className="text-xs text-slate-400 font-bold">{k}</p>
              <p className="font-black text-slate-800 mt-1 break-all">{String(v)}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500">
          Clinqov generates <b>FHIR R4 / Kenya Core |1.0.0</b> for DHA SHR verify (same shape as Slade sandbox samples).
          ICD-11 MMS · LOINC vitals/labs · MOH ORDER-CAT · GE*/INV*/AC* from Terminology masters — not SHA AfyaConnect.
        </p>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <h2 className="font-bold text-slate-800">DHA clinical Bearer token (Admin)</h2>
          <p className="text-xs text-slate-500">
            URLs like <code>/api/v1/clinical/fhir/Patient/…</code> need <code>Authorization: Bearer …</code>.
            Create a 7-day token here (admin only), then open or curl the clinical URL.
            Share links under <code>/api/shr/share/…</code> do not need this header.
          </p>
          <div className="flex flex-wrap gap-2">
            <button className={BTN} disabled={busy} onClick={createDhaToken}>{busy ? 'Working…' : 'Create 7-day Bearer token'}</button>
            {dhaToken?.access_token && (
              <>
                <button className={BTN2} onClick={() => flash('Bearer token', dhaToken.access_token)}>Copy token</button>
                <button className={BTN2} onClick={() => flash('curl', curlExample)}>Copy curl</button>
              </>
            )}
          </div>
          {dhaToken && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-2">
              <p><span className="font-bold">Expires:</span> {new Date(dhaToken.expires_at).toLocaleString()} ({dhaToken.expires_in}s)</p>
              <p className="break-all font-mono text-[11px]"><span className="font-bold">Token:</span> {dhaToken.access_token}</p>
              <p className="text-slate-600">{dhaToken.note}</p>
              <pre className="bg-white border border-slate-200 rounded-lg p-2 overflow-x-auto text-[10px]">{curlExample}</pre>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <h2 className="font-bold text-slate-800">Patient FHIR preview &amp; DHA share links</h2>
          <p className="text-xs text-slate-500">
            Generate the full chart bundle, download JSON, then publish time-limited share URLs (Slade-style <code>/fhir/Condition/…</code>) for DHA to hit on your domain.
          </p>
          <div className="relative">
            <input className={INP} placeholder="Search name, UHID, phone, or CR ID…" value={q} onChange={e => setQ(e.target.value)} />
            {hits.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                {hits.map((p: any) => (
                  <button key={p.id} type="button" className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100" onClick={() => pickPatient(p)}>
                    <span className="font-semibold">{p.firstName || p.first_name} {p.lastName || p.last_name}</span>
                    <span className="text-xs text-slate-500 ml-2">{p.uhid}</span>
                    <span className="block text-[11px] font-mono text-slate-400">{p.shaBeneficiaryCrId || p.sha_beneficiary_cr_id || 'No CR ID'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {patient && (
            <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-2">
              <p><span className="font-bold">{patient.firstName || patient.first_name} {patient.lastName || patient.last_name}</span> · {patient.uhid}</p>
              <p className="text-xs font-mono">HMIS ID: {patient.id}</p>
              <p className="text-xs">Kenya CR ID: {cr ? <span className="font-mono font-bold text-teal-800">{cr}</span> : <span className="text-amber-700">Missing — FHIR preview will fail</span>}</p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <select className={INP} value={encounterId} onChange={e => setEncounterId(e.target.value)}>
                  <option value="">No visit — use patient id as encounter context</option>
                  {visits.map((v: any) => (
                    <option key={v.id || v.reference_id} value={v.id || v.reference_id}>
                      {(v.visitType || v.visit_type || v.type || 'Visit')} {v.id || v.reference_id}
                    </option>
                  ))}
                </select>
                <select className={INP} value={visitType} onChange={e => setVisitType(e.target.value as any)}>
                  <option value="OUTPATIENT">OPD</option>
                  <option value="INPATIENT">IPD</option>
                  <option value="EMERGENCY">ER</option>
                </select>
                <button className={BTN} disabled={busy} onClick={generate}>{busy ? 'Working…' : 'Generate FHIR'}</button>
                <button className={BTN2} disabled={busy || !patient} onClick={publishShare}>Publish share links</button>
              </div>
            </div>
          )}

          {previewErr && <p className="text-sm text-red-700 bg-red-50 rounded-lg p-2">{previewErr}</p>}
          {copied && <p className="text-xs text-teal-700">Copied {copied}</p>}

          {preview && (
            <div className="space-y-3">
              <div className={`rounded-xl p-3 text-sm font-semibold ${ok ? 'bg-green-50 text-green-800 border border-green-200' : generated ? 'bg-amber-50 text-amber-900 border border-amber-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {ok
                  ? `Ready for DHA verify — download shr-bundle-${patient?.uhid || 'UHID'}.json and share links, or submit when SHR sync is enabled.`
                  : generated
                    ? `Chart generated (${entries.length} resources). Fix terminology gaps before calling it DHA-compliant.`
                    : 'Generation failed structurally. Fix identity (CR ID / PUID / facility) first.'}
              </div>
              {verifyChecklist.length > 0 && (
                <div className="border border-slate-200 rounded-xl p-3 space-y-1">
                  <p className="text-xs font-bold text-slate-800 mb-1">DHA verify checklist</p>
                  {verifyChecklist.map((c: any) => (
                    <p key={c.label} className={`text-xs ${c.ok ? 'text-green-800' : 'text-amber-800'}`}>
                      {c.ok ? '✓' : '○'} {c.label}
                    </p>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs bg-slate-100 px-2 py-1 rounded-lg">{entries.length} resources</span>
                <span className="text-xs bg-slate-100 px-2 py-1 rounded-lg">{errors.length} validation errors</span>
                <span className="text-xs bg-slate-100 px-2 py-1 rounded-lg">{issues.length} terminology issues</span>
                <span className="text-xs bg-slate-100 px-2 py-1 rounded-lg">{omitted.length} omitted</span>
                {Object.entries(resourceCounts).map(([t, n]) => (
                  <span key={t} className="text-[10px] bg-teal-50 text-teal-900 px-2 py-1 rounded-lg">{t}×{n as number}</span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {preview.bundle && (
                  <>
                    <button className={BTN2} onClick={() => downloadJson(`shr-bundle-${patient?.uhid || patient?.id}.json`, preview.bundle)}>Download Bundle JSON</button>
                    <button className={BTN2} onClick={() => flash('bundle', JSON.stringify(preview.bundle, null, 2))}>Copy Bundle</button>
                    <button className={BTN2} onClick={() => downloadJson(`shr-bundle-${patient?.uhid || patient?.id}.ndjson`,
                      (preview.bundle.entry || []).map((e: any) => JSON.stringify(e.resource)).join('\n'),
                      'application/fhir+ndjson')}>Download NDJSON</button>
                  </>
                )}
                {selResource && (
                  <>
                    <button className={BTN2} onClick={() => downloadJson(`${selResource.resourceType}-${selResource.id}.json`, selResource)}>Download selected resource</button>
                    <button className={BTN2} onClick={() => flash('resource', JSON.stringify(selResource, null, 2))}>Copy selected JSON</button>
                  </>
                )}
              </div>

              {share && (
                <div className="border border-teal-200 rounded-xl overflow-hidden bg-teal-50/40">
                  <p className="text-xs font-bold bg-teal-100 px-3 py-2">DHA share package (expires {new Date(share.expiresAt).toLocaleString()})</p>
                  <div className="p-3 space-y-2 text-xs">
                    <p className="text-slate-600">{share.note}</p>
                    {share.urls.token && <p className="break-all text-slate-700">Token: {share.urls.token}</p>}
                    <div className="flex flex-wrap gap-2">
                      <a className={BTN2} href={share.urls.bundle} target="_blank" rel="noopener noreferrer">Open share bundle</a>
                      <button className={BTN2} onClick={() => flash('bundle URL', share.urls.bundle)}>Copy share bundle URL</button>
                      <button className={BTN2} onClick={() => flash('all links', (share.urls.clinicalStyle || []).map((x: any) => x.url).join('\n'))}>Copy clinical URLs</button>
                      <button className={BTN2} onClick={() => downloadJson(`shr-share-links-${patient?.uhid}.json`, share)}>Download link list</button>
                    </div>
                    <p className="text-slate-600">
                      Two URL styles: <b>shareUrl</b> opens without a Bearer header (token is in the path).
                      <b> Clinical URL</b> (<code>/api/v1/clinical/fhir/…</code>) needs the 7-day Bearer token above.
                    </p>
                    <div className="max-h-48 overflow-y-auto border border-teal-100 rounded-lg bg-white">
                      {(share.urls.clinicalStyle || []).map((x: any) => (
                        <div key={`${x.type}-${x.id}`} className="px-2 py-1 border-t border-slate-100 space-y-1">
                          <div className="flex gap-2 items-start">
                            <span className="font-bold text-[#02357A] shrink-0">{x.type}</span>
                            <a className="break-all flex-1 text-teal-800 underline" href={x.shareUrl || x.url} target="_blank" rel="noopener noreferrer">{x.shareUrl || x.url}</a>
                            <button type="button" className="text-teal-700 shrink-0" onClick={() => flash(`${x.type} share`, x.shareUrl || x.url)}>Copy share</button>
                          </div>
                          {x.url && x.shareUrl && x.url !== x.shareUrl && (
                            <div className="flex gap-2 items-start pl-0">
                              <span className="text-slate-400 shrink-0 w-16">clinical</span>
                              <span className="break-all flex-1 text-slate-600">{x.url}</span>
                              <button type="button" className="text-teal-700 shrink-0" onClick={() => flash(`${x.type} clinical`, x.url)}>Copy</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {issues.length > 0 && (
                <div className="border border-amber-200 rounded-xl overflow-hidden">
                  <p className="text-xs font-bold bg-amber-50 px-3 py-2">Gaps to fill — Terminology → DHA Masters (GE / INV / AC)</p>
                  {issues.map((i: any, n: number) => (
                    <div key={n} className="px-3 py-2 text-xs border-t border-amber-100">
                      <p className="font-bold">{i.resourceType} · {i.field}</p>
                      <p>Local: {i.localValue}</p>
                      <p>Expected: {i.expectedTerminology}</p>
                    </div>
                  ))}
                </div>
              )}
              {omitted.length > 0 && (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <p className="text-xs font-bold bg-slate-50 px-3 py-2">Omitted</p>
                  {omitted.map((o: any, n: number) => (
                    <p key={n} className="px-3 py-1 text-xs border-t border-slate-100">{o.type} · {o.localId} — {o.reason}</p>
                  ))}
                </div>
              )}
              {errors.filter((e: string) => !String(e).startsWith('TERMINOLOGY_MAPPING_REQUIRED')).length > 0 && (
                <ul className="text-xs text-red-800 list-disc pl-5">
                  {errors.filter((e: string) => !String(e).startsWith('TERMINOLOGY_MAPPING_REQUIRED')).map((e: string, i: number) => <li key={i}>{e}</li>)}
                </ul>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="border border-slate-200 rounded-xl max-h-96 overflow-y-auto">
                  <p className="text-xs font-bold bg-slate-50 px-3 py-2">Resources in bundle</p>
                  {entries.map((en: any, i: number) => {
                    const r = en.resource || en;
                    const profile = r.meta?.profile?.[0] || 'no profile';
                    const code = r.code?.coding?.[0]?.code || r.medicationCodeableConcept?.coding?.[0]?.code || r.class?.code || '';
                    return (
                      <button key={i} type="button" className={`block w-full text-left px-3 py-2 text-xs border-t border-slate-100 hover:bg-slate-50 ${selResource === r ? 'bg-teal-50' : ''}`} onClick={() => setSelResource(r)}>
                        <span className="font-bold text-[#02357A]">{r.resourceType}</span>
                        <span className="font-mono ml-2">{r.id}</span>
                        {code && <span className="ml-2 text-slate-500">{code}</span>}
                        <span className="block text-[10px] text-slate-400 truncate">{profile}</span>
                      </button>
                    );
                  })}
                  {entries.length === 0 && <p className="p-3 text-xs text-slate-400">No bundle yet.</p>}
                </div>
                <pre className="border border-slate-200 rounded-xl p-3 text-[11px] overflow-auto max-h-96 bg-slate-50">
                  {selResource ? JSON.stringify(selResource, null, 2) : (preview.bundle ? 'Select a resource, or download the full bundle.' : 'No FHIR JSON.')}
                </pre>
              </div>

              <div className="border border-slate-200 rounded-xl p-3 text-xs text-slate-600 space-y-1">
                <p className="font-bold text-slate-800">DHA verify alignment (vs Slade / reference bundle)</p>
                <p>· Patient: Kenya CR + national ID + phone identifiers; <code>birthDate</code> as YYYY-MM-DD; profile <code>kenya-core-patient|1.0.0</code></p>
                <p>· Condition: ICD-11 MMS <code>http://id.who.int/icd/release/11/mms</code> (no null coding.version)</p>
                <p>· Observation: LOINC + UCUM (vitals + labs)</p>
                <p>· ServiceRequest: MOH ORDER-CAT Laboratory/Imaging; labs LOINC; imaging INV-* from MOH investigations master</p>
                <p>· Medication / Allergy: map drugs → GE* and allergens → AC* under Terminology → DHA Masters or Pharmacy → Edit drug</p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex justify-between items-center mb-2">
            <h2 className="font-bold text-slate-800">Sync dashboard</h2>
            <button className={BTN2} onClick={loadDash}>Refresh</button>
          </div>
          <p className="text-xs text-slate-500 mb-2">Pending / failed SHR submissions (separate from local FHIR preview).</p>
          <pre className="text-[11px] bg-slate-50 rounded-xl p-3 overflow-auto max-h-40">{JSON.stringify(dash || {}, null, 2)}</pre>
        </div>
      </div>
    </Layout>
  );
}
